import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatHoursHM } from '@/utils/formatHours';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Money, Clock, Plus, Trash, PencilSimple, SpinnerGap, Check, FileXls, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { EmployeeName } from '@/components/EmployeeName';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OvertimeTab({ employees, fetchEmployees }) {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const { empNo } = useEmployeesMap();
  const { getBadge } = useEmployeeBadges(employees);

  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  const [autoSuggestions, setAutoSuggestions] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());  // bulk-approve queue
  const [bulkApproving, setBulkApproving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ user_id: '', hours: '', month: '', year: new Date().getFullYear(), notes: '' });
  const [otFilterMonth, setOtFilterMonth] = useState(new Date().toISOString().slice(5, 7));
  const [otFilterYear, setOtFilterYear] = useState(new Date().getFullYear().toString());
  const [otFilterStart, setOtFilterStart] = useState('');
  const [otFilterEnd, setOtFilterEnd] = useState('');
  const [saving, setSaving] = useState(false);
  // Cycle metadata echoed by /overtime/auto-suggest when in month mode.
  // Used to render "من {cycle_start} إلى {cycle_end}" instead of just
  // the calendar month name — matches the payroll cycle exactly.
  const [cycleStart, setCycleStart] = useState(null);
  const [cycleEnd, setCycleEnd] = useState(null);
  // Approval confirmation dialog — shows hours + amount, lets admin
  // tweak the hours (up/down) before confirming.
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveHours, setApproveHours] = useState('');

  const months = [
    { value: '01', label: t('months.01') }, { value: '02', label: t('months.02') },
    { value: '03', label: t('months.03') }, { value: '04', label: t('months.04') },
    { value: '05', label: t('months.05') }, { value: '06', label: t('months.06') },
    { value: '07', label: t('months.07') }, { value: '08', label: t('months.08') },
    { value: '09', label: t('months.09') }, { value: '10', label: t('months.10') },
    { value: '11', label: t('months.11') }, { value: '12', label: t('months.12') },
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchOvertime();
    fetchAutoSuggestions();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otFilterMonth, otFilterYear, otFilterStart, otFilterEnd]);

  const fetchOvertime = async () => {
    setOvertimeLoading(true);
    try {
      const params = new URLSearchParams();
      // Date-range filter (if either bound is set) takes precedence over month/year.
      if (otFilterStart || otFilterEnd) {
        if (otFilterStart) params.append('start_date', otFilterStart);
        if (otFilterEnd) params.append('end_date', otFilterEnd);
      } else {
        if (otFilterMonth) params.append('month', otFilterMonth);
        if (otFilterYear) params.append('year', otFilterYear);
      }
      const { data } = await axios.get(`${API}/overtime?${params.toString()}`, { withCredentials: true });
      setOvertimeRecords(data);
    } catch (error) {
      console.error('Failed to fetch overtime:', error);
    } finally {
      setOvertimeLoading(false);
    }
  };

  const fetchAutoSuggestions = async () => {
    setAutoLoading(true);
    try {
      const params = new URLSearchParams();
      // Date-range mode takes precedence — lets admins approve overtime
      // incrementally for a custom slice of the month.
      if (otFilterStart || otFilterEnd) {
        if (otFilterStart) params.append('start_date', otFilterStart);
        if (otFilterEnd) params.append('end_date', otFilterEnd);
      } else {
        params.append('month', otFilterMonth);
        params.append('year', otFilterYear);
      }
      const { data } = await axios.get(`${API}/overtime/auto-suggest?${params.toString()}`, { withCredentials: true });
      setAutoSuggestions(data?.suggestions || []);
      setCycleStart(data?.cycle_start || null);
      setCycleEnd(data?.cycle_end || null);
    } catch (error) {
      console.error('Failed to fetch auto-overtime suggestions:', error);
      setAutoSuggestions([]);
    } finally {
      setAutoLoading(false);
    }
  };

  // Always store/query month as a zero-padded 2-digit string (e.g. "06").
  // This matches the filter dropdown values and keeps storage consistent
  // regardless of how a caller derived the month (parseInt vs string).
  const padMonth = (m) => String(parseInt(m, 10) || 0).padStart(2, '0');

  // When approving from a date-range view, we ADD the new hours to the
  // existing month record (incremental). In month-mode the approval
  // REPLACES the month total (a full sync). This is what lets admins say
  // "approve the remaining days I didn't approve last time".
  const isRangeMode = !!(otFilterStart || otFilterEnd);

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const bulkApproveSelected = async () => {
    const toApprove = autoSuggestions.filter((s) => selectedIds.has(s.user_id));
    if (toApprove.length === 0) return;
    if (!window.confirm(language === 'ar'
      ? `سيتم اعتماد الإضافي المقترح لـ ${toApprove.length} موظف. متابعة؟`
      : `Will approve suggested overtime for ${toApprove.length} employee(s). Continue?`)) return;
    setBulkApproving(true);
    let ok = 0, failed = 0;
    for (const s of toApprove) {
      try {
        await approveSuggestion(s, undefined, s.hours);
        ok += 1;
      } catch (_e) {
        failed += 1;
      }
    }
    setBulkApproving(false);
    setSelectedIds(new Set());
    toast.success(language === 'ar'
      ? `تم اعتماد ${ok} من ${toApprove.length}${failed ? ` — فشل: ${failed}` : ''}`
      : `Approved ${ok} of ${toApprove.length}${failed ? ` — failed: ${failed}` : ''}`);
  };

  const approveSuggestion = async (s, mode, customHours) => {
    setApprovingId(s.user_id);
    try {
      // Derive month/year for the OT record. In range mode we use the
      // start_date's month; in month mode we use the filter dropdowns.
      let mYear = parseInt(otFilterYear);
      let mMonth = padMonth(otFilterMonth);
      if (isRangeMode) {
        const ref = otFilterStart || otFilterEnd;
        const d = new Date(ref);
        mYear = d.getFullYear();
        mMonth = String(d.getMonth() + 1).padStart(2, '0');
      }
      const effectiveMode = mode || (isRangeMode ? 'add' : 'replace');
      // `customHours` (from the approval dialog) overrides the suggested
      // hours when the admin edited the value up or down. Otherwise we
      // fall back to the raw suggestion.
      const finalHours = Number(customHours ?? s.hours);
      if (!finalHours || finalHours <= 0) {
        toast.error(language === 'ar' ? 'عدد الساعات يجب أن يكون أكبر من صفر' : 'Hours must be greater than zero');
        setApprovingId(null);
        return;
      }
      const noteSuffix = isRangeMode
        ? `اعتماد فترة · ${otFilterStart || ''} → ${otFilterEnd || ''}`
        : `اعتماد شهري · ${s.total_overtime_minutes} دقيقة من ${s.days_with_overtime} يوم`;
      const adjustedNote = (customHours !== undefined && Math.abs(finalHours - s.hours) > 0.001)
        ? `${noteSuffix} · تعديل يدوي: ${s.hours}س → ${finalHours}س`
        : noteSuffix;
      await axios.post(
        `${API}/overtime/approve-auto`,
        {
          user_id: s.user_id,
          hours: finalHours,
          month: mMonth,
          year: mYear,
          mode: effectiveMode,
          notes: adjustedNote,
        },
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم اعتماد الإضافي بنجاح' : 'Overtime approved successfully');
      await fetchAutoSuggestions();
      await fetchOvertime();
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === 'ar' ? 'فشل الاعتماد' : 'Approval failed'));
    } finally {
      setApprovingId(null);
    }
  };

  // ─── Approval confirmation dialog helpers ───────────────────────────
  // Opens a modal showing the suggested hours + monetary amount, and lets
  // the admin adjust hours up or down before confirming. The amount label
  // recalculates live using the same hourly rate the backend used.
  const openApproveDialog = (s) => {
    setApproveTarget(s);
    setApproveHours(String(s.hours));
  };
  const closeApproveDialog = () => {
    setApproveTarget(null);
    setApproveHours('');
  };
  const confirmApprove = async () => {
    if (!approveTarget) return;
    const hoursNum = parseFloat(approveHours);
    if (!hoursNum || hoursNum <= 0) {
      toast.error(language === 'ar' ? 'عدد الساعات يجب أن يكون أكبر من صفر' : 'Hours must be greater than zero');
      return;
    }
    await approveSuggestion(approveTarget, undefined, hoursNum);
    closeApproveDialog();
  };
  // Live-computed amount preview for the dialog. Uses the same overtime
  // rate the backend returned in the suggestion payload.
  const approvePreviewAmount = approveTarget
    ? Math.max(0, parseFloat(approveHours) || 0) * (approveTarget.overtime_rate || 0)
    : 0;

  const openAdd = () => {
    setEditing(null);
    setForm({ user_id: '', hours: '', month: otFilterMonth, year: parseInt(otFilterYear), notes: '' });
    setShowDialog(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setForm({ user_id: record.user_id, hours: record.hours.toString(), month: record.month, year: record.year, notes: record.notes || '' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.hours || parseFloat(form.hours) <= 0) {
      toast.error(language === 'ar' ? 'يرجى إدخال عدد الساعات' : 'Please enter hours');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/overtime/${editing.id}`, {
          hours: parseFloat(form.hours), notes: form.notes
        }, { withCredentials: true });
        toast.success(t('overtime_updated'));
      } else {
        if (!form.user_id) {
          toast.error(t('select_employee'));
          setSaving(false);
          return;
        }
        await axios.post(`${API}/overtime`, {
          user_id: form.user_id, hours: parseFloat(form.hours),
          month: padMonth(form.month), year: form.year, notes: form.notes,
        }, { withCredentials: true });
        toast.success(t('overtime_added'));
      }
      setShowDialog(false);
      fetchOvertime();
    } catch (error) {
      const msg = error.response?.data?.detail || t('error');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/overtime/${id}`, { withCredentials: true });
      toast.success(t('overtime_deleted'));
      fetchOvertime();
    } catch {
      toast.error(t('error'));
    }
  };

  // ── Approved-overtime export (XLSX / PDF). Fetches the file as a
  // blob so the browser saves it with the server-provided filename.
  const exportApproved = async (kind) => {
    if (!overtimeRecords.length) return;
    const url =
      `${API}/overtime/export.${kind}` +
      `?month=${otFilterMonth}&year=${otFilterYear}&language=${language}`;
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: kind === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `approved_overtime_${otFilterYear}_${otFilterMonth}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
      toast.success(language === 'ar' ? 'تم التصدير' : 'Exported');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل التصدير' : 'Export failed');
    }
  };

  const totalOvertimeAmount = overtimeRecords.reduce((sum, r) => sum + (r.overtime_amount || 0), 0);
  const totalOvertimeHours = overtimeRecords.reduce((sum, r) => sum + (r.hours || 0), 0);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={otFilterMonth} onValueChange={setOtFilterMonth} disabled={!!(otFilterStart || otFilterEnd)}>
            <SelectTrigger className="w-[150px] bg-[#E8E6F5] border-[#C4BFF0]" data-testid="ot-filter-month">
              <SelectValue placeholder={t('select_month')} />
            </SelectTrigger>
            <SelectContent>{months.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={otFilterYear} onValueChange={setOtFilterYear} disabled={!!(otFilterStart || otFilterEnd)}>
            <SelectTrigger className="w-[120px] bg-[#E8E6F5] border-[#C4BFF0]" data-testid="ot-filter-year">
              <SelectValue placeholder={t('select_year')} />
            </SelectTrigger>
            <SelectContent>{years.map((y) => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}</SelectContent>
          </Select>
          <div className="h-7 w-px bg-[#655dc6]/20 mx-1" />
          <span className="text-xs text-[#655dc6]/70 font-medium">
            {language === 'ar' ? 'أو حسب الفترة:' : 'Or by period:'}
          </span>
          <input
            type="date"
            value={otFilterStart}
            onChange={(e) => setOtFilterStart(e.target.value)}
            className="h-9 px-2 text-sm rounded-md border border-[#C4BFF0] bg-white text-[#655dc6]"
            data-testid="ot-filter-start"
            aria-label={language === 'ar' ? 'من تاريخ' : 'From date'}
          />
          <span className="text-[#655dc6]/60 text-sm" aria-hidden>
            {language === 'ar' ? '←' : '→'}
          </span>
          <input
            type="date"
            value={otFilterEnd}
            onChange={(e) => setOtFilterEnd(e.target.value)}
            className="h-9 px-2 text-sm rounded-md border border-[#C4BFF0] bg-white text-[#655dc6]"
            data-testid="ot-filter-end"
            aria-label={language === 'ar' ? 'إلى تاريخ' : 'To date'}
          />
          {(otFilterStart || otFilterEnd) && (
            <button
              type="button"
              onClick={() => { setOtFilterStart(''); setOtFilterEnd(''); }}
              className="text-xs text-[#655dc6] hover:underline px-2"
              data-testid="ot-filter-clear-range"
            >
              {language === 'ar' ? 'إلغاء الفترة' : 'Clear range'}
            </button>
          )}
        </div>
        <Button onClick={openAdd} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="add-overtime-btn">
          <Plus size={20} className="me-2" weight="bold" />
          {t('add_overtime')}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border border-[#C4BFF0]">
          <CardContent className="pt-5 pb-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#655dc6]/10 flex items-center justify-center">
              <Clock size={24} weight="duotone" className="text-[#655dc6]" />
            </div>
            <div>
              <p className="text-sm text-[#655dc6]/60">{t('overtime_hours')}</p>
              <p className="text-2xl font-bold text-[#655dc6]" data-testid="total-ot-hours">{formatHoursHM(totalOvertimeHours)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#C4BFF0]">
          <CardContent className="pt-5 pb-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#059669]/10 flex items-center justify-center">
              <Money size={24} weight="duotone" className="text-[#059669]" />
            </div>
            <div>
              <p className="text-sm text-[#059669]/60">{t('overtime_amount')}</p>
              <p className="text-2xl font-bold text-[#059669]" data-testid="total-ot-amount">{totalOvertimeAmount.toLocaleString()} {t('sar')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-amber-200 bg-amber-50/40" data-testid="auto-overtime-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-amber-800 text-base flex items-center gap-2">
              <Clock size={20} weight="duotone" />
              {language === 'ar' ? 'اقتراحات الإضافي الفعلي — بعد نهاية اليوم' : 'Auto-suggested Overtime — End-of-day Actual'}
              {isRangeMode && (
                <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-normal" data-testid="range-mode-badge">
                  {language === 'ar' ? 'وضع الفترة (إضافة)' : 'Range (incremental)'}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {autoSuggestions.some(s => !s.already_added || s.hours > 0) && (
                <Button
                  type="button"
                  size="sm"
                  onClick={async () => {
                    const pending = autoSuggestions.filter(s => isRangeMode ? s.hours > 0 : !s.already_added);
                    if (pending.length === 0) return;
                    for (const s of pending) {
                      // Sequential to avoid hammering the API and keep
                      // toast messages coherent.
                      // eslint-disable-next-line no-await-in-loop
                      await approveSuggestion(s);
                    }
                  }}
                  disabled={autoLoading || approvingId !== null}
                  className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                  data-testid="approve-all-auto-ot-btn"
                >
                  {language === 'ar' ? 'اعتماد الكل' : 'Approve all'}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={fetchAutoSuggestions}
                disabled={autoLoading}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                data-testid="refresh-auto-ot-btn"
              >
                {autoLoading
                  ? <SpinnerGap size={14} className="animate-spin" />
                  : (language === 'ar' ? 'تحديث' : 'Refresh')}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-amber-700/80 mt-1 leading-relaxed">
            {language === 'ar'
              ? 'يُحتسب الإضافي تلقائياً بعد انصراف الموظف فقط، ويُحدَّد بالفرق بين الساعات الفعلية للعمل والساعات اليومية المتعاقد عليها. الأيام التي لم يبصم الانصراف فيها لا تُحتسب.'
              : 'Overtime is auto-computed only after clock-out, as the difference between actual worked hours and contracted daily hours. Days without a clock-out are skipped.'}
            {isRangeMode && (
              <span className="block mt-1 text-amber-900 font-medium">
                {language === 'ar'
                  ? '⓵ في وضع الفترة: الاعتماد يُضاف إلى الساعات المعتمدة مسبقاً (لاعتماد باقي الأيام).'
                  : '⓵ Range mode: approvals are ADDED to previously-approved hours (use to top-up remaining days).'}
              </span>
            )}
          </p>
          {/*
            Cycle window pill — shown only in month mode when the backend
            echoed cycle_start/cycle_end. Tells the admin the ACTUAL window
            the aggregation used (e.g. "من 2026-06-26 إلى 2026-07-25"),
            not just the calendar month.
          */}
          {!isRangeMode && cycleStart && cycleEnd && (
            <div
              className="mt-2 inline-flex items-center gap-2 rounded-md bg-amber-100 border border-amber-300 px-2.5 py-1 text-[11px] font-medium text-amber-900"
              data-testid="ot-cycle-window"
            >
              <Clock size={12} weight="duotone" />
              {language === 'ar'
                ? `دورة الاستحقاق: من ${cycleStart} إلى ${cycleEnd}`
                : `Cycle: ${cycleStart} → ${cycleEnd}`}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {autoLoading ? (
            <div className="text-center py-6 text-amber-700/60 text-xs">
              <SpinnerGap size={20} className="animate-spin inline" />
            </div>
          ) : autoSuggestions.length === 0 ? (
            <p className="text-center py-4 text-xs text-amber-700/60" data-testid="no-auto-ot">
              {language === 'ar' ? 'لا توجد اقتراحات إضافي تلقائي لهذه الفترة' : 'No auto-overtime suggestions for this period'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              {/* Bulk approve toolbar — appears when there are selected rows. */}
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between bg-amber-100/60 border border-amber-300 rounded-md px-3 py-2 mb-2" data-testid="ot-bulk-toolbar">
                  <span className="text-xs text-amber-900 font-medium">
                    {language === 'ar' ? `تم اختيار ${selectedIds.size} موظف` : `${selectedIds.size} employee(s) selected`}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-xs text-amber-900/70 hover:text-amber-900 underline"
                      data-testid="ot-bulk-clear"
                    >
                      {language === 'ar' ? 'إلغاء الاختيار' : 'Clear'}
                    </button>
                    <Button
                      size="sm"
                      onClick={bulkApproveSelected}
                      disabled={bulkApproving}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-8 gap-1"
                      data-testid="ot-bulk-approve"
                    >
                      {bulkApproving
                        ? <SpinnerGap size={14} className="animate-spin" />
                        : <Check size={14} weight="bold" />}
                      {language === 'ar' ? `اعتماد المحدَّدين (${selectedIds.size})` : `Approve Selected (${selectedIds.size})`}
                    </Button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36px] text-center">
                      <input
                        type="checkbox"
                        checked={autoSuggestions.length > 0 && selectedIds.size === autoSuggestions.length}
                        onChange={(e) => setSelectedIds(e.target.checked
                          ? new Set(autoSuggestions.map((s) => s.user_id))
                          : new Set())}
                        className="cursor-pointer"
                        data-testid="ot-select-all"
                      />
                    </TableHead>
                    <TableHead className="text-start text-amber-900">{language === 'ar' ? 'الرقم الوظيفي' : 'Emp. No.'}</TableHead>
                    <TableHead className="text-start text-amber-900">{language === 'ar' ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'دقائق إضافية' : 'OT minutes'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'أيام' : 'Days'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'الساعات' : 'Hours'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'معتمد سابقاً' : 'Already approved'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'المبلغ المقترح' : 'Suggested Amount'}</TableHead>
                    <TableHead className="text-center text-amber-900">{language === 'ar' ? 'إجراء' : 'Action'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...autoSuggestions]
                    // v1.5.92 — sort by employee_number ascending so the
                    // admin reviews the queue in the same order used in
                    // every other HR module (payroll, attendance, OT).
                    .sort((a, b) => {
                      const na = parseInt(a.employee_number, 10);
                      const nb = parseInt(b.employee_number, 10);
                      if (!isNaN(na) && !isNaN(nb)) return na - nb;
                      return String(a.employee_number || '').localeCompare(String(b.employee_number || ''));
                    })
                    .map(s => {
                    const existingH = Number(s.existing_approved_hours || 0);
                    const inSync = s.already_added && !isRangeMode && Math.abs(existingH - s.hours) < 0.01;
                    const buttonLabel = isRangeMode
                      ? (language === 'ar' ? `إضافة +${formatHoursHM(s.hours)}` : `Add +${formatHoursHM(s.hours)}`)
                      : (s.already_added
                        ? (language === 'ar' ? `مزامنة → ${formatHoursHM(s.hours)}` : `Sync → ${formatHoursHM(s.hours)}`)
                        : (language === 'ar' ? 'اعتماد' : 'Approve'));
                    return (
                      <TableRow key={s.user_id} data-testid={`auto-ot-row-${s.user_id}`}>
                        <TableCell className="w-[36px] text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(s.user_id)}
                            onChange={() => toggleSelect(s.user_id)}
                            disabled={inSync}
                            className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`ot-select-${s.user_id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-[#655dc6]/80" data-testid={`auto-ot-empno-${s.user_id}`}>{empNo(s.user_id) || '-'}</TableCell>
                        <TableCell className="text-start">
                          <EmployeeName
                            name={s.user_name}
                            nameEn={s.user_name_en}
                            badgeData={getBadge(s.user_id)}
                            badgeLanguage={language}
                          />
                          {s.department && <div className="text-[10px] text-gray-500 mt-0.5">{s.department}</div>}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">{s.total_overtime_minutes}</TableCell>
                        <TableCell className="text-center text-xs">{s.days_with_overtime}</TableCell>
                        <TableCell className="text-center font-semibold text-amber-800">
                          {Array.isArray(s.daily_breakdown) && s.daily_breakdown.length > 0 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 hover:bg-amber-100 focus:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-help underline decoration-dotted decoration-amber-400 underline-offset-2"
                                  data-testid={`ot-hours-trigger-${s.user_id}`}
                                  aria-label={language === 'ar' ? 'عرض ساعات الإضافي لكل يوم' : 'View overtime hours per day'}
                                >
                                  {formatHoursHM(s.hours)}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="center"
                                side="top"
                                sideOffset={6}
                                className="w-[280px] p-0 border-amber-200"
                                data-testid={`ot-hours-popover-${s.user_id}`}
                              >
                                <div className="px-3 py-2 border-b border-amber-200 bg-amber-50/70 rounded-t-md">
                                  <div className="text-[11px] font-semibold text-amber-900">
                                    {language === 'ar' ? 'ساعات الإضافي التفصيلية' : 'Overtime hours breakdown'}
                                  </div>
                                  <div className="text-[10px] text-amber-700/80 mt-0.5">
                                    {language === 'ar'
                                      ? `${s.daily_breakdown.length} يوم — إجمالي ${formatHoursHM(s.hours)}`
                                      : `${s.daily_breakdown.length} day(s) — total ${formatHoursHM(s.hours)}`}
                                  </div>
                                </div>
                                <div className="max-h-[260px] overflow-y-auto">
                                  <table className="w-full text-[11px]">
                                    <thead className="sticky top-0 bg-white border-b border-amber-100">
                                      <tr className="text-amber-900/80">
                                        <th className="text-start px-3 py-1.5 font-medium">
                                          {language === 'ar' ? 'التاريخ' : 'Date'}
                                        </th>
                                        <th className="text-end px-3 py-1.5 font-medium">
                                          {language === 'ar' ? 'ساعات' : 'Hours'}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {s.daily_breakdown.map((d, i) => (
                                        <tr
                                          key={`${d.date}-${i}`}
                                          className="border-b border-amber-50 last:border-b-0 hover:bg-amber-50/50"
                                          data-testid={`ot-hours-row-${s.user_id}-${i}`}
                                        >
                                          <td className="text-start px-3 py-1.5 font-mono text-gray-700" dir="ltr">
                                            {d.date}
                                          </td>
                                          <td className="text-end px-3 py-1.5 font-mono font-semibold text-amber-800">
                                            {formatHoursHM(d.overtime_hours)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-amber-50/60 border-t border-amber-200">
                                        <td className="text-start px-3 py-1.5 font-semibold text-amber-900">
                                          {language === 'ar' ? 'الإجمالي' : 'Total'}
                                        </td>
                                        <td className="text-end px-3 py-1.5 font-mono font-bold text-amber-900">
                                          {formatHoursHM(s.hours)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span>{formatHoursHM(s.hours)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-xs" data-testid={`existing-ot-${s.user_id}`}>
                          {existingH > 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-mono">
                              {formatHoursHM(existingH)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold text-emerald-700">
                          {s.suggested_amount.toLocaleString()} {t('sar')}
                        </TableCell>
                        <TableCell className="text-center">
                          {inSync ? (
                            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                              {language === 'ar' ? '✓ معتمد' : '✓ Approved'}
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => openApproveDialog(s)}
                              disabled={approvingId === s.user_id}
                              className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`approve-auto-ot-${s.user_id}`}
                            >
                              {approvingId === s.user_id
                                ? <SpinnerGap size={12} className="animate-spin" />
                                : buttonLabel}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-[#C4BFF0]">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <Clock size={22} weight="duotone" className="text-[#655dc6]" />
              {t('overtime_tab')} - {months.find(m => m.value === otFilterMonth)?.label} {otFilterYear}
            </CardTitle>
            {overtimeRecords.length > 0 && (
              <div className="flex items-center gap-2" data-testid="ot-export-buttons">
                <Button
                  size="sm" variant="outline"
                  className="h-8 gap-1.5 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => exportApproved('xlsx')}
                  data-testid="ot-export-xlsx"
                >
                  <FileXls size={14} weight="fill" />
                  {language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="h-8 gap-1.5 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => exportApproved('pdf')}
                  data-testid="ot-export-pdf"
                >
                  <FilePdf size={14} weight="fill" />
                  {language === 'ar' ? 'تصدير PDF' : 'Export PDF'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#655dc6]/50 mb-4">{t('overtime_formula')}</p>
          {overtimeLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="overtime-table">
                <TableHeader>
                  <TableRow className="border-[#C4BFF0]">
                    <TableHead className="text-start">{t('employee_number')}</TableHead>
                    <TableHead className="text-start">{t('employee_name')}</TableHead>
                    <TableHead className="text-start">{t('basic_salary')}</TableHead>
                    <TableHead className="text-start">{t('hourly_rate')}</TableHead>
                    <TableHead className="text-start">{t('overtime_rate')} (×1.5)</TableHead>
                    <TableHead className="text-start">{t('overtime_hours')}</TableHead>
                    <TableHead className="text-start">{t('overtime_amount')}</TableHead>
                    <TableHead className="text-start">{t('overtime_note')}</TableHead>
                    <TableHead className="text-start">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overtimeRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-[#655dc6]/70">{t('no_data')}</TableCell>
                    </TableRow>
                  ) : (
                    overtimeRecords.map((record) => (
                      <TableRow key={record.id} className="border-[#C4BFF0]" data-testid={`ot-row-${record.id}`}>
                        <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(record.user_id)}</TableCell>
                        <TableCell><EmployeeName name={record.user_name} nameEn={record.user_name_en} badgeData={getBadge(record.user_id)} badgeLanguage={language} /></TableCell>
                        <TableCell className="text-[#655dc6]/70">{record.basic_salary?.toLocaleString()}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{record.hourly_rate?.toFixed(2)}</TableCell>
                        <TableCell className="text-blue-600 font-medium">{record.overtime_rate?.toFixed(2)}</TableCell>
                        <TableCell className="font-semibold text-[#655dc6]">{formatHoursHM(record.hours)}</TableCell>
                        <TableCell className="font-bold text-[#059669]">{record.overtime_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-[#655dc6]/50 text-sm max-w-[150px] truncate">{record.notes || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#655dc6] hover:bg-[#E8E6F5]"
                              onClick={() => openEdit(record)} data-testid={`edit-ot-${record.id}`}>
                              <PencilSimple size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(record.id)} data-testid={`delete-ot-${record.id}`}>
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[450px]" dir={direction} data-testid="overtime-dialog" aria-describedby="overtime-dialog-desc">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock size={24} weight="duotone" className="text-[#655dc6]" />
                {editing ? t('edit_overtime') : t('add_overtime')}
              </DialogTitle>
              <DialogDescription id="overtime-dialog-desc">
                {t('overtime_formula')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {!editing && (
                <div className="space-y-2">
                  <Label>{t('select_employee')}</Label>
                  <Select value={form.user_id} onValueChange={(v) => setForm(p => ({ ...p, user_id: v }))}>
                    <SelectTrigger data-testid="ot-employee-select"><SelectValue placeholder={t('select_employee')} /></SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editing && (
                <div className="space-y-2">
                  <Label>{t('employee_name')}</Label>
                  <Input value={editing.user_name} disabled className="bg-gray-50" />
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('hours_count')}</Label>
                <Input type="number" step="0.5" min="0.5" value={form.hours}
                  onChange={e => setForm(p => ({ ...p, hours: e.target.value }))}
                  placeholder="10" dir="ltr" data-testid="ot-hours-input" />
              </div>
              {!editing && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('select_month')}</Label>
                    <Select value={form.month} onValueChange={v => setForm(p => ({ ...p, month: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{months.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('select_year')}</Label>
                    <Select value={form.year.toString()} onValueChange={v => setForm(p => ({ ...p, year: parseInt(v) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{years.map((y) => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('overtime_note')}</Label>
                <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder={language === 'ar' ? 'ملاحظات اختيارية' : 'Optional notes'} data-testid="ot-notes-input" />
              </div>
              <div className="bg-[#E8E6F5] rounded-lg p-3 text-xs text-[#655dc6]/70">
                {t('overtime_formula')}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={handleSave} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-overtime-btn">
                {saving ? <SpinnerGap size={20} className="animate-spin" /> : t('save')}
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)}>{t('cancel')}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Approval Confirmation Dialog ═══════════════════════════════════
          Shown when the admin clicks "اعتماد" on an auto-suggested row.
          Displays hours + computed amount; hours are editable so the admin
          can bump them up/down before finalising. */}
      <Dialog open={!!approveTarget} onOpenChange={(open) => { if (!open) closeApproveDialog(); }}>
        <DialogContent
          className="max-w-md"
          dir={direction}
          data-testid="approve-ot-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
              <Clock size={20} weight="duotone" />
              {language === 'ar' ? 'اعتماد الإضافي' : 'Approve Overtime'}
            </DialogTitle>
            <DialogDescription>
              {approveTarget && (
                <>
                  <span className="font-semibold text-gray-800">
                    <EmployeeName name={approveTarget.user_name} nameEn={approveTarget.user_name_en} />
                  </span>
                  {approveTarget.department && (
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {approveTarget.department}
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {approveTarget && (
            <div className="space-y-4 py-2">
              {/* Suggested value (read-only reference) */}
              <div className="rounded-lg bg-[#F8F7FC] border border-[#C4BFF0] p-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-[#655dc6]/70">
                    {language === 'ar' ? 'المُقترَح تلقائياً' : 'Auto-suggested'}
                  </span>
                  <span className="font-mono font-semibold text-[#655dc6]" data-testid="approve-ot-suggested">
                    {formatHoursHM(approveTarget.hours)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-[#655dc6]/70 text-xs">
                    {language === 'ar' ? 'من عدد الأيام' : 'From # of days'}
                  </span>
                  <span className="text-xs text-[#655dc6]/70">{approveTarget.days_with_overtime}</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-[#655dc6]/70 text-xs">
                    {language === 'ar' ? 'سعر الساعة الإضافي' : 'Overtime rate/hour'}
                  </span>
                  <span className="text-xs text-[#655dc6]/70">
                    {(approveTarget.overtime_rate || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {t('sar')}
                  </span>
                </div>
              </div>

              {/* Editable hours */}
              <div>
                <Label htmlFor="approve-hours-input" className="text-sm text-gray-700 mb-1 block">
                  {language === 'ar' ? 'ساعات الاعتماد (قابلة للتعديل)' : 'Hours to approve (editable)'}
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const cur = parseFloat(approveHours) || 0;
                      setApproveHours(Math.max(0, +(cur - 0.5).toFixed(2)).toString());
                    }}
                    className="h-9 w-9 p-0 text-[#655dc6] border-[#C4BFF0]"
                    data-testid="approve-hours-decrement"
                    aria-label={language === 'ar' ? 'إنقاص' : 'Decrement'}
                  >
                    −
                  </Button>
                  <Input
                    id="approve-hours-input"
                    type="number"
                    step="0.25"
                    min="0"
                    value={approveHours}
                    onChange={(e) => setApproveHours(e.target.value)}
                    className="text-center font-mono text-lg font-semibold text-[#655dc6] border-[#C4BFF0]"
                    data-testid="approve-hours-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const cur = parseFloat(approveHours) || 0;
                      setApproveHours((+(cur + 0.5).toFixed(2)).toString());
                    }}
                    className="h-9 w-9 p-0 text-[#655dc6] border-[#C4BFF0]"
                    data-testid="approve-hours-increment"
                    aria-label={language === 'ar' ? 'زيادة' : 'Increment'}
                  >
                    +
                  </Button>
                </div>
                {/* H:MM equivalent so the admin sees "1:25" alongside 1.42 */}
                <div className="text-[11px] text-[#655dc6]/70 mt-1 text-center font-mono">
                  {language === 'ar'
                    ? `≈ ${formatHoursHM(approveHours)} ساعة (س:د)`
                    : `≈ ${formatHoursHM(approveHours)} (h:mm)`}
                </div>
              </div>

              {/* Live-computed amount */}
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-700 text-sm">
                    {language === 'ar' ? 'المبلغ المستحق' : 'Amount payable'}
                  </span>
                  <span
                    className="font-mono text-lg font-bold text-emerald-800"
                    data-testid="approve-ot-preview-amount"
                  >
                    {approvePreviewAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} {t('sar')}
                  </span>
                </div>
                {Math.abs((parseFloat(approveHours) || 0) - approveTarget.hours) > 0.001 && (
                  <div className="mt-1 text-[11px] text-amber-700">
                    {language === 'ar'
                      ? `⚠ سيتم اعتماد ${formatHoursHM(approveHours)} بدل ${formatHoursHM(approveTarget.hours)} المقترحة`
                      : `⚠ Approving ${formatHoursHM(approveHours)} instead of the suggested ${formatHoursHM(approveTarget.hours)}`}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeApproveDialog}
              data-testid="approve-ot-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              type="button"
              onClick={confirmApprove}
              disabled={approvingId !== null || !parseFloat(approveHours)}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
              data-testid="approve-ot-confirm"
            >
              {approvingId
                ? <SpinnerGap size={14} className="animate-spin" />
                : (language === 'ar' ? 'تأكيد الاعتماد' : 'Confirm approval')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
